import { useState } from "react";
import { Logo } from "./Logo";
import { Menu, X } from "lucide-react";

interface HeaderProps {
  showFixed: boolean;
}

export const Header: React.FC<HeaderProps> = ({ showFixed }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  const navLinks = [
    { href: "/#how-it-works", label: "How It Works" },
    { href: "/#for-your-team", label: "For Your Team" },
    { href: "/#features", label: "Features" },
    { href: "/#pricing", label: "Pricing" },
    { 
      href: "https://docs.korrero.com", 
      label: "Docs", 
      external: true 
    }
  ];

  const headerContent = (
    <>
      {/* Left */}
      <div className="flex items-center gap-2 w-auto">
        <Logo className="h-6 text-primary" />
      </div>

      {/* Center - Desktop Navigation */}
      <nav className="hidden show-nav-desktop items-center justify-center space-x-4 lg:space-x-6 xl:space-x-8 text-theme-secondary font-medium w-full max-w-2xl mx-auto">
        {navLinks.map((link, index) => (
          <a
            key={index}
            href={link.href}
            target={link.external ? "_blank" : undefined}
            rel={link.external ? "noopener noreferrer" : undefined}
            className="hover:text-theme-primary hover:text-shadow-white transition-all duration-150 ease-in-out"
            onClick={link.external ? undefined : () => setMobileMenuOpen(false)}
          >
            <span>{link.label}</span>
            {link.external && (
              <span className="ml-1 inline-block text-[10px] opacity-70 group-hover:opacity-100">↗</span>
            )}
          </a>
        ))}
      </nav>

      {/* Right */}
      <div className="flex items-center justify-end space-x-2 sm:space-x-4">
        {/* Mobile menu button */}
        <button 
          className="hide-nav-desktop p-2 text-theme-primary" 
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <a
          href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=sign_in_button"
          className="hidden sm:inline-block px-4 sm:px-6 py-2 sm:py-3 border border-theme rounded-full hover:bg-white hover:text-primary hover:box-shadow-white transition-colors duration-150 ease-in-out text-sm sm:text-base"
          target="_blank"
          rel="noopener noreferrer"
        >
          Sign In
        </a>
        <a
          href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=get_started_free_button"
          className="px-4 sm:px-6 py-2 sm:py-3 bg-[var(--color-bg-inverse-paper)] text-[var(--color-text-inverse-default)] border border-theme rounded-full hover:bg-primary hover:text-white hover:border-white/20 transition-colors duration-150 ease-in-out font-medium text-sm sm:text-base"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get Started
        </a>
      </div>
    </>
  );

  return (
    <>
      {/* Static header (visible only when at the very top) */}
              <div
          className={`grid grid-cols-2 grid-nav-desktop items-center px-4 sm:px-6 md:px-9 py-3 sm:py-4 border-b border-theme backdrop-blur-md transition-all duration-500 ease-out ${
          showFixed ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        {headerContent}
      </div>

      {/* Fixed header (visible only while scrolling up) */}
              <div
          className={`fixed top-0 left-0 right-0 z-50 grid grid-cols-2 grid-nav-desktop items-center px-4 sm:px-6 md:px-9 py-3 sm:py-4 border-b border-theme backdrop-blur-md shadow-lg transition-all duration-300 ease-out ${
          showFixed ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        {headerContent}
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-[var(--color-bg-default)]/70 backdrop-blur-sm transition-all duration-300 hide-nav-desktop ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute left-0 top-3 sm:top-4 w-full px-4 sm:px-6 md:px-9 flex items-center justify-between">
          <Logo className="h-6 text-primary" />
          <button 
            className="hide-nav-desktop p-2 text-theme-primary" 
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        <div className="flex flex-col items-center justify-center h-full space-y-6 p-6 text-center">
          {navLinks.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target={link.external ? "_blank" : undefined}
              rel={link.external ? "noopener noreferrer" : undefined}
              className="text-xl font-medium hover:text-theme-primary transition-all duration-150 ease-in-out"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>{link.label}</span>
              {link.external && (
                <span className="ml-1 inline-block text-xs opacity-70">↗</span>
              )}
            </a>
          ))}
          <div className="pt-4 flex flex-col space-y-4 w-full max-w-xs">
            <a
              href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=sign_in_button"
              className="px-6 py-3 border border-theme rounded-full hover:bg-white hover:text-primary hover:box-shadow-white transition-colors duration-150 ease-in-out w-full text-center"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sign In
            </a>
            <a
              href="https://console.korrero.com/?utm_source=landing&utm_medium=landing&utm_campaign=get_started_free_button"
              className="px-6 py-3 bg-[var(--color-bg-inverse-paper)] text-[var(--color-text-inverse-default)] border border-theme rounded-full hover:bg-primary hover:text-white hover:border-white/20 transition-colors duration-150 ease-in-out font-medium w-full text-center"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Started Free
            </a>
          </div>
        </div>
      </div>
    </>
  );
};